/**
 * Date helpers
 * ============
 *
 * Small date utilities used for the delivery-delay tracking in ./orderStatus.js.
 *
 * The promised delivery date is NOT calculated here - the storefront works it
 * out at checkout and freezes it onto the order as `expectedDeliveryDate`, so
 * the OMS only ever reads it. That is deliberate: the date is the promise the
 * customer was shown, and recalculating it later would silently change it
 * whenever a product's delivery latency or the delivery rules were edited.
 *
 * All dates here are handled in Gulf Standard Time, and calendar dates are
 * represented as midnight UTC so that a date is a plain day with no time
 * component that could drift across timezones.
 */

/* =========================================================================
 * TUNABLE SETTINGS - safe to edit
 * ========================================================================= */

/** Gulf Standard Time is UTC+4. Same convention used in pages/Details.js. */
export const GULF_OFFSET_HOURS = 4

/* =========================================================================
 * Internals
 * ========================================================================= */

const MS_PER_HOUR = 60 * 60 * 1000
export const MS_PER_DAY = 24 * MS_PER_HOUR
const GULF_OFFSET_MS = GULF_OFFSET_HOURS * MS_PER_HOUR

export const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"]
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"]

/**
 * Shift a UTC instant into Gulf time. After shifting, read the value with the
 * getUTC* accessors (getUTCDay, getUTCHours, ...) and you get the Gulf-local
 * day and hour. We do it this way, rather than with the plain local getters, so
 * the result does not change based on the timezone of the machine viewing OMS.
 */
const toGulfTime = (date) => new Date(date.getTime() + GULF_OFFSET_MS)

/** Midnight of the current Gulf calendar day. */
export const getGulfToday = () => {
    const now = toGulfTime(new Date())
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
}

/**
 * Parse a stored Strapi `date` field ("2026-07-21") into midnight UTC.
 *
 * Anchored to UTC on purpose. `new Date("2026-07-21T00:00:00")` without the Z
 * would be read in the browser's local timezone and could land on the 20th, so
 * we pin it explicitly. The slice also tolerates the field coming back as a
 * full ISO timestamp.
 */
export const parseStoredDate = (value) => {
    if (!value) return null
    const parsed = new Date(`${String(value).slice(0, 10)}T00:00:00Z`)
    return isNaN(parsed.getTime()) ? null : parsed
}

/** True for a real, usable Date. Guards every helper below against bad input. */
const isValidDate = (value) =>
    value instanceof Date && !isNaN(value.getTime())

/**
 * Date only, as "2026-07-21". Safe to store: it carries no time component, so
 * it cannot drift a day when read back in another timezone.
 */
export const toISODate = (date) =>
    isValidDate(date) ? date.toISOString().slice(0, 10) : null

/** Whole days from `from` to `to`. Negative when `to` is the earlier date. */
export const daysBetween = (from, to) =>
    Math.round((to.getTime() - from.getTime()) / MS_PER_DAY)

/**
 * Format a delivery date for display, e.g. "21 Jul 2026".
 * Uses the getUTC* accessors because dates here are midnight UTC.
 */
export const formatDeliveryDate = (date) => isValidDate(date)
    ? `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`
    // ? `${WEEKDAYS[date.getUTCDay()]}, ${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`
    : ""
